import {
  MAX_SUPPORT_MESSAGE_LENGTH,
  MAX_SUPPORT_SUBJECT_LENGTH,
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_LABELS,
  type SupportCategory,
} from '@groundly/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CircleCheck, Clock, MessageSquareReply } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useOpenSupportTicket, useSupportTickets } from '../../../api/support.js';
import { useWebsites } from '../../../api/websites.js';
import { Button } from '../../../components/Button.js';
import { DangerAlert } from '../../../components/DangerAlert.js';
import { PageHeading } from '../../../components/PageHeading.js';
import { Pagination } from '../../../components/Pagination.js';
import { LoadingArea } from '../../../components/PlaceholderPage.js';
import { StatusBadge } from '../../../components/StatusBadge.js';
import { TextField } from '../../../components/TextField.js';
import { formatFullDateTime } from '../../../conversations/display.js';
import { parseConversationsSearch, searchForPage } from '../../../conversations/search.js';
import { clientStatus } from '../../../support/display.js';
import styles from '../../../support/support.module.css';

/**
 * `/support`: ask Groundly for help, and the account's requests so far
 * (`product/business-rules.md`, Support). Open to every Account User, with no
 * permission: anyone in the account can get stuck.
 */
export const Route = createFileRoute('/_app/support/')({
  validateSearch: parseConversationsSearch,
  component: SupportPage,
});

const STATUS_ICONS = { attention: MessageSquareReply, neutral: Clock, success: CircleCheck };

function SupportPage() {
  const { page = 1 } = Route.useSearch();
  const navigate = useNavigate();
  const tickets = useSupportTickets(page);
  const [writing, setWriting] = useState(false);

  return (
    <>
      <header className={styles.header}>
        <div>
          <PageHeading>Support</PageHeading>
          <p className={styles.caption}>Ask Groundly for help. We reply here and by email.</p>
        </div>
        {!writing ? (
          <Button variant="primary" onClick={() => setWriting(true)}>
            New request
          </Button>
        ) : null}
      </header>

      {writing ? <NewRequest onCancel={() => setWriting(false)} /> : null}

      {tickets.isError ? (
        <DangerAlert
          title="Your requests didn't load"
          action={<Button onClick={() => void tickets.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : tickets.isPending ? (
        <LoadingArea />
      ) : tickets.data.total === 0 ? (
        writing ? null : (
          <div className={styles.empty}>
            <h2>No requests yet</h2>
            <p>
              Stuck on billing, the assistant&apos;s answers, crawling or anything else? Send a
              request and the Groundly team will get back to you.
            </p>
          </div>
        )
      ) : (
        <>
          <ul className={styles.list}>
            {tickets.data.items.map((ticket) => {
              const status = clientStatus(ticket.status);
              return (
                <li key={ticket.id} className={styles.row}>
                  <Link
                    className={styles.rowLink}
                    to="/support/$ticketId"
                    params={{ ticketId: ticket.id }}
                  >
                    <span className={styles.number}>#{ticket.number}</span>
                    <span className={styles.subject}>{ticket.subject}</span>
                  </Link>
                  <StatusBadge tone={status.tone} icon={STATUS_ICONS[status.tone]}>
                    {status.label}
                  </StatusBadge>
                  <span className={styles.meta}>
                    {SUPPORT_CATEGORY_LABELS[ticket.category]}
                    {ticket.websiteName !== null ? ` · ${ticket.websiteName}` : ''} ·{' '}
                    {ticket.openedBy} · updated{' '}
                    <time dateTime={ticket.lastMessageAt}>
                      {formatFullDateTime(ticket.lastMessageAt)}
                    </time>
                  </span>
                </li>
              );
            })}
          </ul>
          <Pagination
            page={tickets.data.page}
            pageSize={tickets.data.pageSize}
            total={tickets.data.total}
            onPageChange={(next) => void navigate({ to: '.', search: searchForPage(next) })}
          />
        </>
      )}
    </>
  );
}

function NewRequest({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const websites = useWebsites();
  const open = useOpenSupportTicket();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportCategory>('other');
  const [websiteId, setWebsiteId] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = {
      ...(subject.trim() === '' ? { subject: 'Add a subject.' } : {}),
      ...(message.trim() === '' ? { message: 'Describe what you need help with.' } : {}),
    };
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    open.mutate(
      { subject, category, websiteId: websiteId === '' ? null : websiteId, message },
      {
        onSuccess: (ticket) =>
          void navigate({ to: '/support/$ticketId', params: { ticketId: ticket.id } }),
      },
    );
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h2 className={styles.formTitle}>New support request</h2>
      <TextField
        id="support-subject"
        label="Subject"
        maxLength={MAX_SUPPORT_SUBJECT_LENGTH}
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        error={errors.subject}
      />
      <div className={styles.pair}>
        <label className={styles.field}>
          <span className={styles.label}>What it&apos;s about</span>
          <select
            className={styles.select}
            value={category}
            onChange={(event) => setCategory(event.target.value as SupportCategory)}
          >
            {SUPPORT_CATEGORIES.map((key) => (
              <option key={key} value={key}>
                {SUPPORT_CATEGORY_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Website (optional)</span>
          <select
            className={styles.select}
            value={websiteId}
            onChange={(event) => setWebsiteId(event.target.value)}
          >
            <option value="">Not about one website</option>
            {(websites.data?.items ?? []).map((website) => (
              <option key={website.id} value={website.id}>
                {website.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className={styles.field} htmlFor="support-message">
        <span className={styles.label}>Message</span>
      </label>
      <textarea
        id="support-message"
        className={styles.textarea}
        rows={6}
        maxLength={MAX_SUPPORT_MESSAGE_LENGTH}
        value={message}
        aria-invalid={errors.message === undefined ? undefined : true}
        aria-describedby="support-message-hint"
        onChange={(event) => setMessage(event.target.value)}
      />
      <p className={styles.hint} id="support-message-hint">
        {errors.message ??
          'What happened, what you expected, and the page or conversation if there is one.'}
      </p>
      {open.isError ? (
        <DangerAlert title="Your request wasn't sent">
          Check your connection and try again.
        </DangerAlert>
      ) : null}
      <div className={styles.actions}>
        <Button variant="primary" type="submit" disabled={open.isPending}>
          {open.isPending ? 'Sending…' : 'Send request'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
